import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, Check, Star } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { waInspectorHref, GOOGLE_REVIEW_URL } from "@/lib/business";
import { trackEvent } from "@/lib/analytics";
import api from "@/lib/api";

const reportItems = [
  ["Water staining to ceiling", "Bathroom 2 · adjacent to plumbing", "High", "bg-[#E11D2E] text-white"],
  ["Grout and silicone gaps", "Kitchen · worktop junction", "Medium", "bg-[#FF6A00] text-white"],
  ["AC temperature differential", "Living area · vs. setpoint", "Medium", "bg-[#FF6A00] text-white"],
];

export function InspectorBand() {
  return (
    <section className="py-20 lg:py-24 bg-soft border-y border-line" data-testid="inspector-band">
      <div className="wrap grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <span className="eyebrow">Behind every report</span>
          <h2 className="text-[clamp(1.85rem,3.4vw,2.5rem)] mt-3 mb-4">A professional inspector. A report reviewed for clarity.</h2>
          <p className="text-muted mb-6 max-w-[46ch]">Your inspection is completed methodically, room by room, using equipment appropriate to the agreed scope. Every report is human-reviewed for clarity and completeness before it is released to you.</p>
          <ul className="flex flex-col gap-3 mb-8">
            {["Methodical, room-by-room inspection", "Equipment used where the scope requires it", "Human review before every report is released", "Reachable on WhatsApp before and after"].map((t) => (
              <li key={t} className="flex items-center gap-2.5 text-bodytext font-medium"><Check size={18} className="text-blue flex-none" /> {t}</li>
            ))}
          </ul>
          <a href={waInspectorHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-[10px] border border-line bg-white text-ink font-semibold hover:border-wa hover:text-wa transition-colors">Talk to an inspector</a>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="rounded-[20px] overflow-hidden border border-line shadow-md aspect-[4/3]">
            <picture>
              <source type="image/avif" srcSet="/inspector-1-600.avif 600w, /inspector-1-900.avif 900w" sizes="(max-width: 1024px) 100vw, 50vw" />
              <source type="image/webp" srcSet="/inspector-1-600.webp 600w, /inspector-1-900.webp 900w" sizes="(max-width: 1024px) 100vw, 50vw" />
              <img src="/inspector-1-900.webp" alt="Illustrative inspection scene" width={900} height={600} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </picture>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function SampleReportBlock() {
  return (
    <section className="py-20 lg:py-24 bg-white" data-testid="sample-report-block">
      <div className="wrap grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <span className="eyebrow">Your inspection report</span>
          <h2 className="text-[clamp(1.85rem,3.4vw,2.5rem)] mt-3 mb-4">Clear Findings for Your Next Decision</h2>
          <p className="text-muted mb-4 max-w-[46ch]">Fedari documents relevant visible findings with photographic evidence where practicable and clear priority ratings. Your report helps you raise specific observations with the developer, seller or responsible party.</p>
          <p className="text-[0.8rem] text-muted mb-6 max-w-[46ch]">Inspections are visual and non-destructive and cover the agreed safe, accessible areas. Reports do not guarantee the identification of concealed or future defects, developer repairs or warranty coverage.</p>
          <ul className="flex flex-col gap-3 mb-7">
            {["Findings organised by location", "Photographic evidence of visible findings where practicable", "Low / Medium / High priority ratings", "Report delivery within your confirmed timeframe"].map((t) => (
              <li key={t} className="flex items-center gap-2.5 text-bodytext font-medium"><Check size={18} className="text-blue flex-none" /> {t}</li>
            ))}
          </ul>
          <Link to="/sample-snagging-report" data-testid="report-sample-link" onClick={() => trackEvent("sample_report_click", { location: "sample-report-block" })} className="inline-flex items-center gap-2 bg-blue text-white px-6 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">View a Sample Report <ArrowRight size={16} /></Link>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="grid grid-cols-[1fr,132px] sm:grid-cols-[1fr,180px] gap-4 items-start">
            <div className="bg-white border border-line rounded-[20px] shadow-lg overflow-hidden">
            <div className="bg-soft border-b border-line px-6 py-5 flex justify-between items-center">
              <div>
                <div className="font-mono-jb text-[0.6rem] tracking-wider text-blue uppercase">Sample report · 88 findings</div>
                <div className="font-display text-[1.05rem] mt-1">Sample inspection report</div>
              </div>
              <span className="font-mono-jb text-[0.66rem] bg-blue-50 text-blue px-2 py-1 rounded">DIGITAL</span>
            </div>
            <div className="px-6 py-5">
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[["25", "Low", "text-sev-low"], ["52", "Medium", "text-sev-med"], ["11", "High", "text-sev-high"]].map(([n, l, c]) => (
                  <div key={l} className="bg-soft rounded-lg p-3"><b className={`text-2xl font-extrabold block leading-none ${c}`}>{n}</b><span className="text-[0.66rem] text-muted uppercase tracking-wide">{l}</span></div>
                ))}
              </div>
              {reportItems.map(([t, loc, sev, cls]) => (
                <div key={t} className="flex gap-3 py-3 border-t border-soft">
                  <div className="w-13 h-10 min-w-[52px] rounded-md bg-soft grid place-items-center text-muted"><FileText size={16} /></div>
                  <div className="flex-1"><b className="text-[0.88rem] text-ink block">{t}</b><span className="text-[0.76rem] text-muted">{loc}</span></div>
                  <span className={`font-mono-jb text-[0.62rem] uppercase tracking-wide px-1.5 py-1 rounded h-max ${cls}`}>{sev}</span>
                </div>
              ))}
              <div className="mt-3 text-[0.64rem] text-muted font-mono-jb tracking-wide uppercase">Findings shown are illustrative · summary counts from the approved sample</div>
            </div>
            </div>
            <figure>
              <div className="overflow-hidden rounded-[14px] border border-line bg-white shadow-sm">
                <picture>
                  <source type="image/avif" srcSet="/images/fedari-certificate-700.avif 700w, /images/fedari-certificate-1051.avif 1051w" sizes="(max-width: 640px) 128px, 180px" />
                  <source type="image/webp" srcSet="/images/fedari-certificate-700.webp 700w, /images/fedari-certificate-1051.webp 1051w" sizes="(max-width: 640px) 128px, 180px" />
                  <img src="/images/fedari-certificate-700.webp" alt="Sample Fedari inspection clearance document" width="1055" height="1491" loading="lazy" decoding="async" className="w-full h-auto" data-testid="sample-report-certificate" />
                </picture>
              </div>
              <figcaption className="text-[0.68rem] text-muted mt-2 leading-snug">Sample clearance document — confirms the observed status of previously reported findings within the agreed scope. Not a government approval, certification of the whole property or a guarantee.</figcaption>
            </figure>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function ReviewsBlock() {
  const [reviews, setReviews] = useState(null);
  useEffect(() => { api.get("/reviews").then((r) => setReviews(r.data.reviews || [])).catch(() => setReviews([])); }, []);

  const display = (reviews || []).slice(0, 6);
  const hasReviews = display.length > 0;

  return (
    <section className="py-20 lg:py-24 bg-soft border-y border-line" data-testid="reviews-block">
      <div className="wrap">
        <SectionHead eyebrow="Client feedback" title="Feedback from verified Fedari clients." />
        {hasReviews ? (
          <>
            <div className="grid md:grid-cols-3 gap-4">
              {display.map((r, i) => (
                <Reveal key={r.id || i} delay={(i % 3) * 0.06}>
                  <div className="bg-white border border-line rounded-[14px] p-6 h-full">
                    {r.rating ? <div className="text-[#f5a623] tracking-widest mb-3">{"★".repeat(Math.max(1, Math.min(5, r.rating)))}</div> : null}
                    <p className="text-bodytext text-[0.95rem] leading-relaxed mb-5">“{r.comment}”</p>
                    <div className="flex items-center gap-3 border-t border-line pt-4">
                      <span className="w-9 h-9 rounded-full bg-blue-50 grid place-items-center font-bold text-[0.8rem] text-blue-600">{(r.name || "?").slice(0, 2).toUpperCase()}</span>
                      <div><div className="text-sm font-semibold text-ink">{r.name}</div><div className="text-[0.75rem] text-muted">{r.location || ""}{r.date ? ` · ${r.date}` : ""}</div></div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-6">
              <p className="text-[0.78rem] text-muted">Verified reviews from Fedari Inspection clients.</p>
              {GOOGLE_REVIEW_URL && (
                <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("review_click", { location: "reviews" })} data-testid="google-review-link"
                  className="sm:ml-auto inline-flex items-center gap-2 text-sm font-semibold text-blue border border-line rounded-[10px] px-4 py-2.5 bg-white hover:border-blue transition-colors">
                  <Star size={15} /> Leave a Google review
                </a>
              )}
            </div>
          </>
        ) : (
          <Reveal className="rounded-[14px] border border-line bg-white p-8 flex flex-col md:flex-row md:items-center gap-5 justify-between">
            <p className="text-bodytext max-w-[60ch]">We publish verified client reviews as they are collected. For references or to discuss your inspection, message an inspector directly on WhatsApp.</p>
            <div className="flex flex-col sm:flex-row gap-3 flex-none">
              {GOOGLE_REVIEW_URL && (
                <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("review_click", { location: "reviews-empty" })} data-testid="google-review-link"
                  className="inline-flex items-center gap-2 border border-line text-blue px-6 py-3.5 rounded-[10px] font-semibold hover:border-blue transition-colors"><Star size={16} /> Leave a Google review</a>
              )}
              <a href={waInspectorHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-blue text-white px-6 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">Talk to an inspector <ArrowRight size={16} /></a>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
